import { formatCurrency } from "lib/globalize-util";
import type { IScope } from "reducers/scope";
import { IProductMediaAsset } from "constants/assets";
import { getImageUrl as getImage } from "components/customization-modal/helpers";
import type { IProductStyleInsight, IPricing, IRank, IPriceTemplate } from "types/insights/product-style-insight";

export class ProductStyleInsights implements IProductStyleInsight {
    constructor(productStyleInsight: IProductStyleInsight) {
        this.product_id = productStyleInsight.product_id;
        this.name = productStyleInsight.name;
        this.color = productStyleInsight.color;
        this.brand = productStyleInsight.brand;
        this.season = productStyleInsight.season;
        this.category = productStyleInsight.category;
        this.subcategory = productStyleInsight.subcategory;
        this.style_number = productStyleInsight.style_number;
        this.department = productStyleInsight.department;
        this.pricing = productStyleInsight.pricing;
        this.rank = productStyleInsight.rank;
        this.images = productStyleInsight.images;
        this.media = productStyleInsight.media;
        this.cdn = this.formatCdn(this.product_id);
        this.price_templates = productStyleInsight.price_templates;
    }

    product_id: string;
    name: string;
    color: string;
    category: string;
    subcategory: string;
    brand: string;
    season: string;
    style_number: string;
    department: string;
    pricing: IPricing[];
    rank: IRank;
    images: string[];
    media: IProductMediaAsset[];
    cdn: string;
    price_templates: IPriceTemplate[];

    formatCdn(imageId: string = ""): string {
        return imageId ? `//cdn${(imageId.charCodeAt(imageId.length - 1) % 4) + 1}.nuorder.com` : "//cdn1.nuorder.com";
    }

    getImageUrl(scope: IScope): string {
        const hasUnlimitedImages = scope.brand?.getPreference?.("unlimited_images");

        if (hasUnlimitedImages && this.media?.length > 0) {
            return this.media?.[0]?.url;
        }

        if (!hasUnlimitedImages && this.images.length > 0) {
            return getImage(this.cdn, "product", this.images[0]);
        }

        if (this.cdn && scope.brand?.settings?.missing_logo_image) {
            return getImage(this.cdn, "brands", scope.brand?.settings?.missing_logo_image);
        }

        return "";
    }

    getWholesalePriceForCurrency(currency: string): number {
        return this.calculatePriceForCurrency(currency, "wholesale");
    }

    getMsrpPriceForCurrency(currency: string): number {
        return this.calculatePriceForCurrency(currency, "retail");
    }

    private calculatePriceForCurrency(currency: string, priceType: "retail" | "wholesale"): number {
        if (this.price_templates?.length > 0) {
            return formatCurrency(this.price_templates[0]?.[priceType], currency);
        }

        const pricing = this.pricing.filter(
            (pricing) => pricing.disabled === false && pricing.currency_code === currency,
        )[0];

        return pricing ? formatCurrency(pricing[priceType], currency) : null;
    }
}
